import { put, all, takeLatest, call } from 'redux-saga/effects';
import { success } from 'react-toastify-redux';
import { makeRequest } from '@humanspace/infrastructure';

import actions from './actions';
import types from './types';

// ── localStorage helpers ──

function getLocalStorageKey(projectId) {
  return `cc_zones_${projectId}`;
}

function loadLocalZones(projectId) {
  try {
    const raw = localStorage.getItem(getLocalStorageKey(projectId));
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function saveLocalZones(projectId, zones) {
  try {
    localStorage.setItem(getLocalStorageKey(projectId), JSON.stringify(zones));
  } catch (e) {
    // ignore
  }
}

function generateId() {
  return 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// ── ProjectData API helpers (server-side persistence) ──

function fetchServerZones(projectId) {
  return makeRequest({ path: `/projects/${projectId}/data/cc_zones` })
    .then(resp => {
      // API returns the JSON value directly, or 204 (no content) if not set
      if (resp && Array.isArray(resp)) return resp;
      if (resp && resp.data && Array.isArray(resp.data)) return resp.data;
      return [];
    })
    .catch(() => []);
}

function syncZonesToServer(projectId, zones) {
  return makeRequest({
    path: `/projects/${projectId}/data/cc_zones`,
    method: 'PUT',
    body: { value: zones },
    json: true,
  }).catch(() => {
    // silent fail — localStorage is the primary store
  });
}

// ── Zones ──

function* getZonesProcess(action) {
  const projectId = action.payload;

  // 1. Check localStorage first
  let zones = loadLocalZones(projectId);

  // 2. If localStorage is empty, try loading from server
  if (zones.length === 0) {
    try {
      const serverZones = yield call(fetchServerZones, projectId);
      if (serverZones && serverZones.length > 0) {
        zones = serverZones;
        // Cache to localStorage for fast future loads
        saveLocalZones(projectId, zones);
      }
    } catch (e) {
      // ignore — stay with empty
    }
  }

  yield put(actions.getZonesSuccess(zones));
}

function* createZoneProcess(action) {
  const { projectId, zoneData } = action.payload;
  const newZone = { ...zoneData, id: generateId(), projectId };
  const existing = loadLocalZones(projectId);
  existing.push(newZone);
  saveLocalZones(projectId, existing);
  yield put(actions.createZoneSuccess(newZone));
  yield put(success('Zone created'));
  yield put(actions.getZonesSuccess(existing));

  // Sync to server in background
  try { yield call(syncZonesToServer, projectId, existing); } catch (e) { /* ignore */ }
}

function* updateZoneProcess(action) {
  const { zoneId, zoneData } = action.payload;

  // Search all projects in localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('cc_zones_')) {
      const zones = JSON.parse(localStorage.getItem(key) || '[]');
      const idx = zones.findIndex(z => z.id === zoneId);
      if (idx !== -1) {
        zones[idx] = { ...zones[idx], ...zoneData };
        localStorage.setItem(key, JSON.stringify(zones));
        yield put(actions.updateZoneSuccess(zones[idx]));
        yield put(success('Zone updated'));
        yield put(actions.getZonesSuccess(zones));

        // Sync to server — extract projectId from the key
        const projectId = key.replace('cc_zones_', '');
        try { yield call(syncZonesToServer, projectId, zones); } catch (e) { /* ignore */ }
        return;
      }
    }
  }
  yield put(actions.updateZoneFailure('Zone not found'));
}

function* deleteZoneProcess(action) {
  const { zoneId, projectId } = action.payload;
  const zones = loadLocalZones(projectId).filter(z => z.id !== zoneId);
  saveLocalZones(projectId, zones);
  yield put(actions.deleteZoneSuccess());
  yield put(success('Zone deleted'));
  yield put(actions.getZonesSuccess(zones));

  // Sync to server in background
  try { yield call(syncZonesToServer, projectId, zones); } catch (e) { /* ignore */ }
}

// ── UBHPS Features (localStorage + server sync) ──

function fetchServerFeatures(projectId) {
  return makeRequest({ path: `/projects/${projectId}/data/ubhps_features` })
    .then(resp => {
      if (resp && typeof resp === 'object' && !Array.isArray(resp)) return resp;
      if (resp && resp.data && typeof resp.data === 'object') return resp.data;
      return {};
    })
    .catch(() => ({}));
}

function syncFeaturesToServer(projectId, allFeatures) {
  return makeRequest({
    path: `/projects/${projectId}/data/ubhps_features`,
    method: 'PUT',
    body: { value: allFeatures },
    json: true,
  }).catch(() => {
    // silent fail — localStorage is the primary store
  });
}

function gatherAllLocalFeatures(zones) {
  const all = {};
  (zones || []).forEach(z => {
    try {
      const saved = localStorage.getItem(`ubhps_features_${z.id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
          all[z.id] = parsed;
        }
      }
    } catch (e) { /* ignore */ }
  });
  return all;
}

function* getUbhpsFeaturesProcess(action) {
  const { projectId, zoneId } = action.payload;
  try {
    // Check localStorage first
    const saved = localStorage.getItem(`ubhps_features_${zoneId}`);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
        yield put(actions.getUbhpsFeaturesSuccess(parsed));
        return;
      }
    }

    // localStorage empty — try server
    if (projectId) {
      const serverData = yield call(fetchServerFeatures, projectId);
      if (serverData && serverData[zoneId] && Object.keys(serverData[zoneId]).length > 0) {
        // Hydrate localStorage for this zone (and any others from server)
        Object.entries(serverData).forEach(([zId, feats]) => {
          try {
            localStorage.setItem(`ubhps_features_${zId}`, JSON.stringify(feats));
          } catch (e) { /* ignore */ }
        });
        yield put(actions.getUbhpsFeaturesSuccess(serverData[zoneId]));
        return;
      }
    }

    yield put(actions.getUbhpsFeaturesSuccess({}));
  } catch {
    yield put(actions.getUbhpsFeaturesSuccess({}));
  }
}

function* updateUbhpsFeaturesProcess(action) {
  const { projectId, zoneId, features } = action.payload;
  try {
    // Save to localStorage immediately
    localStorage.setItem(`ubhps_features_${zoneId}`, JSON.stringify(features));
    yield put(actions.updateUbhpsFeaturesSuccess());
    yield put(success('Brain Health features updated'));

    // Gather all zones' features and sync to server
    if (projectId) {
      const zones = loadLocalZones(projectId);
      const allFeatures = gatherAllLocalFeatures(zones);
      // Ensure current zone's features are included (in case zones list is stale)
      allFeatures[zoneId] = features;
      try { yield call(syncFeaturesToServer, projectId, allFeatures); } catch (e) { /* ignore */ }
    }
  } catch (err) {
    yield put(actions.updateUbhpsFeaturesFailure(err));
  }
}

export default function* watchCitiesCommunities() {
  yield all([
    takeLatest(types.GET_ZONES, getZonesProcess),
    takeLatest(types.CREATE_ZONE, createZoneProcess),
    takeLatest(types.UPDATE_ZONE, updateZoneProcess),
    takeLatest(types.DELETE_ZONE, deleteZoneProcess),
    takeLatest(types.GET_UBHPS_FEATURES, getUbhpsFeaturesProcess),
    takeLatest(types.UPDATE_UBHPS_FEATURES, updateUbhpsFeaturesProcess),
  ]);
}
